// Gate logic for Morning Anchor's ``screen-followthrough`` step.
// Pulled out of Protocol.jsx into a pure function so it can be unit-
// tested cleanly without mocking the entire Protocol component tree
// (AuthContext, react-router, 6+ api clients, Sentry, localStorage).
//
// The step should only render when the user committed to screens-off
// the previous local-calendar day. The source of truth is the most
// recent ``protocol_inputs`` row with ``step_type='screen_commitment'``
// — if its created_at maps to "yesterday" in the user's local timezone,
// the commitment counts; otherwise it doesn't.

// User-local YYYY-MM-DD. Duplicated from Protocol.jsx rather than
// imported so this file stays tree-shakeable and test-focused; see
// ``screenFollowthrough.test.js`` for the invariants.
export function localDateKey(d = new Date()) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Parse a server-supplied ``YYYY-MM-DD`` string as a *local* calendar
 * date. ``new Date("2026-08-17")`` parses as UTC midnight and shifts a
 * day for anyone west of Greenwich, which is precisely the skew
 * ``ProtocolInput.date`` exists to avoid — so build the date explicitly.
 *
 * @param {string} key - ``YYYY-MM-DD``
 * @returns {Date|null} local midnight on that date, or null if unparseable
 */
export function parseLocalDateKey(key) {
  if (typeof key !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Whole local-calendar days between a row's ``date`` and today.
 * 0 = today, 1 = yesterday, negative = future (clock skew).
 *
 * @param {string} key - ``YYYY-MM-DD``
 * @param {Date} [now=new Date()]
 * @returns {number|null} null when the key is missing or malformed
 */
export function localDaysAgo(key, now = new Date()) {
  const then = parseLocalDateKey(key);
  if (!then) return null;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - then) / 86400000);
}

/**
 * True iff the given ``protocol_inputs`` row's ``date`` field
 * equals the user-local "yesterday".
 *
 * NOTE: this is no longer the morning-resurface gate — see
 * ``resurfacedIntention`` below for why. It is kept because it is a
 * meaningful, well-tested predicate in its own right, and because the
 * age labelling is built on top of it.
 *
 * @param {Object|null|undefined} row - row from ``/protocol-inputs/recent``
 * @param {Date} [now=new Date()] - Current time, injectable for tests.
 * @returns {boolean}
 */
export function isFromYesterday(row, now = new Date()) {
  if (!row || !row.date) return false;
  return localDaysAgo(row.date, now) === 1;
}

/**
 * Build the morning/midday "evening intention" resurface card from the
 * most-recent ``evening_intention`` row.
 *
 * Why this replaced a strict ``date === yesterday`` gate:
 *   The old gate dropped the intention entirely unless it was dated
 *   exactly yesterday. That silently discarded two ordinary cases —
 *   (a) a wind-down completed *after* local midnight, which buckets to
 *   today, and (b) any gap of two or more days, i.e. every user who
 *   skips a night. Both rendered nothing at all, with no empty state,
 *   which is the "it never resurfaces" report.
 *
 *   The gate was originally added to stop the card claiming "Yesterday
 *   you said" over four-day-old text. That concern is real, but the fix
 *   for stale *framing* is accurate framing, not withholding the
 *   content. So: always surface the most recent intention, and let the
 *   label carry its age honestly.
 *
 * @param {Object|null|undefined} row - row from ``/protocol-inputs/recent``,
 *   or null/undefined when the fetch 404'd or errored.
 * @param {Date} [now=new Date()] - Current time, injectable for tests.
 * @returns {{text: string, label: string, daysAgo: number}|null}
 *   null when there is nothing to show (no row, or blank text).
 */
export function resurfacedIntention(row, now = new Date()) {
  const text = typeof row?.input_text === "string" ? row.input_text.trim() : "";
  if (!text) return null;

  // A row with a missing/malformed date still has content worth showing;
  // fall back to the neutral label rather than dropping the intention.
  const daysAgo = localDaysAgo(row.date, now);
  if (daysAgo === null) return { text, label: "Your last intention", daysAgo: null };

  // <= 1 day covers both an ordinary 10pm wind-down (dated yesterday)
  // and a post-midnight one (dated today). Negative = clock skew ahead
  // of us; treat it as current rather than showing a nonsense age.
  if (daysAgo <= 1) return { text, label: "Last night you said", daysAgo };

  const then = parseLocalDateKey(row.date);
  if (daysAgo < 7) {
    const weekday = then.toLocaleDateString("en-US", { weekday: "long" });
    return { text, label: `Your last intention \u00b7 ${weekday}`, daysAgo };
  }
  const stamp = then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return { text, label: `Your last intention \u00b7 ${stamp}`, daysAgo };
}


/**
 * Decide whether Morning Anchor's screen-followthrough step should
 * render, given the most recent ``screen_commitment`` row from the
 * server (or null if none) and the current time.
 *
 * Returns:
 *   ``true``  — yes, render the step (yesterday-local commitment exists)
 *   ``false`` — no, hide the step (no row, or row is too old / too new)
 *   ``null``  — fetch not resolved yet; caller should default to "show"
 *               to avoid a flicker while the network is in flight
 *
 * @param {Object|null|undefined} row - The most-recent
 *   screen_commitment row from ``GET /protocol-inputs/recent``,
 *   or ``null``/``undefined`` when the fetch returned 404 or errored.
 *   Must have a ``created_at`` ISO-8601 string if present.
 * @param {Date} [now=new Date()] - Current time, injectable for tests.
 * @returns {boolean}
 */
export function hasYesterdayCommitment(row, now = new Date()) {
  if (!row || !row.created_at) return false;

  const committedAt = new Date(row.created_at);
  if (Number.isNaN(committedAt.getTime())) return false;

  const committedLocalDate = localDateKey(committedAt);
  const yesterdayLocalDate = localDateKey(
    new Date(now.getTime() - 24 * 60 * 60 * 1000)
  );
  return committedLocalDate === yesterdayLocalDate;
}
